import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import swal from "sweetalert";
import api from "../api/api";
import FarmSelect from "../components/FarmSelect";
import PageTitle from "../components/common/PageTitle";
import "../components/FieldSettingForms/AgriculturalWizardForms.css";

export default function AddCropInfo(props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isExternalUpdate = useRef(false);
  const previousCropData = useRef(null);

  const { isWizardMode, formData, onChange } = props;
  const parentFieldId = props.fieldId;
  const parentFieldName = props.fieldName;
  const parentZoneId = props.zoneId;
  const parentZoneName = props.zoneName;
  const {
    fieldId,
    fieldName,
    zoneId,
    zoneName,
  } = location.state || {};

  const [validated, setValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOptionsLoading, setIsOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState("");
  const [listCrop, setListCrop] = useState([]);
  const [allVarieties, setAllVarieties] = useState([]);
  const [zones, setZones] = useState([]);
  const [checked, setChecked] = useState(false);
  const [displayDate, setDisplayDate] = useState("");
  const [cropData, setCropData] = useState({
    field_uid: parentFieldId || fieldId || "",
    zone_uid: parentZoneId || zoneId || "",
    cropType: "",
    variety: "",
    cropVariety: [],
    plantingDate: "",
    growingDate: "",
    rootDepth: "",
    days: "",
    ecartInter: "",
    ecartIntra: "",
    density: "",
    ruPratique: "",
    kcList: [],
    surface: "",
    crop_nature: "",
  });

  useEffect(() => {
    if (!isWizardMode || !formData) return;

    isExternalUpdate.current = true;
    const nextData = {
      ...cropData,
      ...formData,
      field_uid: parentFieldId || fieldId || formData.field_uid || "",
      zone_uid: parentZoneId || zoneId || formData.zone_uid || "",
    };
    previousCropData.current = nextData;
    setCropData(nextData);
  }, [
    isWizardMode,
    formData,
    parentFieldId,
    parentZoneId,
    fieldId,
    zoneId,
  ]);

  useEffect(() => {
    if (!parentZoneId || parentZoneId === cropData.zone_uid) return;
    isExternalUpdate.current = true;
    setCropData((previous) => ({ ...previous, zone_uid: parentZoneId }));
  }, [parentZoneId, cropData.zone_uid]);

  useEffect(() => {
    if (!isWizardMode || !onChange || isExternalUpdate.current) {
      isExternalUpdate.current = false;
      return;
    }

    const previous = JSON.stringify(previousCropData.current);
    const current = JSON.stringify(cropData);
    if (previous !== current) {
      previousCropData.current = { ...cropData };
      onChange(cropData);
    }
  }, [cropData, isWizardMode, onChange]);

  useEffect(() => {
    if (!cropData.growingDate) {
      setDisplayDate("");
      return;
    }

    const date = new Date(cropData.growingDate);
    if (Number.isNaN(date.getTime())) {
      setDisplayDate("");
      return;
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    setDisplayDate(`${day}/${month}`);
  }, [cropData.growingDate]);

  useEffect(() => {
    let active = true;

    const loadOptions = async () => {
      setIsOptionsLoading(true);
      setOptionsError("");
      try {
        const [cropResponse, varietiesResponse] = await Promise.all([
          api.get("/croptype/list-crop"),
          api.get("/varieties/get-varieties"),
        ]);
        if (!active) return;
        setListCrop(cropResponse.data.Croptype || []);
        setAllVarieties(
          varietiesResponse.data.type === "success"
            ? varietiesResponse.data.Varieties || []
            : []
        );
      } catch {
        if (active) {
          setOptionsError(t("crop_options_load_error"));
        }
      } finally {
        if (active) setIsOptionsLoading(false);
      }
    };

    loadOptions();
    return () => {
      active = false;
    };
  }, [t]);

  useEffect(() => {
    let active = true;

    const loadZones = async () => {
      if (!cropData.field_uid) {
        setZones([]);
        return;
      }

      try {
        const response = await api.post("/field", {
          field_uid: cropData.field_uid,
        });
        if (active) setZones(response.data.field.zones || []);
      } catch {
        if (active) setZones([]);
      }
    };

    loadZones();
    return () => {
      active = false;
    };
  }, [cropData.field_uid]);

  const handleVarietyPick = (event) => {
    setCropData((previous) => ({
      ...previous,
      variety: event.target.value,
    }));
  };

  const handleCropPick = (event) => {
    const crop = listCrop.find(
      (item) => String(item.id) === String(event.target.value)
    );
    if (!crop) {
      setCropData((previous) => ({
        ...previous,
        cropType: "",
        variety: "",
        cropVariety: [],
      }));
      return;
    }

    const varieties = allVarieties
      .filter((variety) => variety.crop_id === crop.id)
      .map((variety) => ({
        varietyId: variety.id,
        variety: variety.crop_variety,
      }));

    let calculatedGrowingDate = "";
    if (crop.plant_date) {
      const plantDate = new Date(crop.plant_date);
      const today = new Date();
      let growingDate = new Date(
        today.getFullYear(),
        plantDate.getMonth(),
        plantDate.getDate()
      );
      if (growingDate < today) {
        growingDate = new Date(
          today.getFullYear() + 1,
          plantDate.getMonth(),
          plantDate.getDate()
        );
      }
      calculatedGrowingDate = growingDate.toISOString().split("T")[0];
    }

    setCropData((previous) => ({
      ...previous,
      cropType: crop.id,
      variety: "",
      cropVariety: varieties,
      rootDepth: crop.root_max,
      ruPratique: crop.practical_fraction,
      days: crop.total,
      growingDate: calculatedGrowingDate,
      kcList: crop.all_kc,
      crop_nature: crop.crop_nature,
    }));
  };

  const handleGrowingSeasonChange = (event) => {
    let input = event.target.value.replace(/\D/g, "").slice(0, 4);
    if (input.length >= 2) {
      const day = Math.min(31, Math.max(1, Number(input.slice(0, 2))));
      input = String(day).padStart(2, "0") + input.slice(2);
    }
    if (input.length >= 4) {
      const month = Math.min(12, Math.max(1, Number(input.slice(2, 4))));
      input = input.slice(0, 2) + String(month).padStart(2, "0");
    }

    const formatted =
      input.length > 2 ? `${input.slice(0, 2)}/${input.slice(2)}` : input;
    setDisplayDate(formatted);

    if (input.length !== 4) {
      setCropData((previous) => ({ ...previous, growingDate: "" }));
      return;
    }

    const today = new Date();
    const seasonDate = new Date(
      today.getFullYear(),
      Number(input.slice(2, 4)) - 1,
      Number(input.slice(0, 2))
    );
    if (seasonDate < today) {
      seasonDate.setFullYear(today.getFullYear() + 1);
    }
    setCropData((previous) => ({
      ...previous,
      growingDate: seasonDate.toISOString().split("T")[0],
    }));
  };

  const addCrop = async () => {
    const data = {
      zone_uid: cropData.zone_uid || parentZoneId || zoneId,
      field_uid: cropData.field_uid || parentFieldId || fieldId,
      croptype_id: cropData.cropType,
      previous_type: cropData.previous_type,
      plantingDate: cropData.plantingDate,
      rootDepth: cropData.rootDepth,
      days: cropData.days,
      crop_variety_id: cropData.variety,
      practical_fraction: cropData.ruPratique,
      density: cropData.density,
      ecart_inter: cropData.ecartInter,
      ecart_intra: cropData.ecartIntra,
      surface: cropData.surface,
      growingDate: cropData.growingDate,
    };
    const response = await api.post("/crop/add-crop", data);

    if (response.data.type === "danger") {
      throw new Error(response.data.message || t("Error"));
    }

    if (response.data.type === "success" && !isWizardMode) {
      const willContinue = await swal({
        title: t("crop_added"),
        text: t("Would you like to continue to create an irrigation type?"),
        icon: "success",
        buttons: {
          cancel: t("No"),
          confirm: { text: t("Yes"), value: true },
        },
      });
      navigate(
        willContinue ? "/add-irrigation" : "/AddField",
        willContinue
          ? {
              state: {
                fieldId: cropData.field_uid || fieldId,
                fieldName: parentFieldName || fieldName,
                zoneId: cropData.zone_uid || zoneId,
                zoneName: parentZoneName || zoneName,
              },
            }
          : undefined
      );
    }

    return response.data;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (!form.checkValidity() || !cropData.zone_uid) {
      event.stopPropagation();
      setValidated(true);
      if (!cropData.zone_uid) {
        swal(t("Error"), t("Please select a crop zone before submitting."), "error");
      }
      return;
    }

    if (isWizardMode) {
      onChange?.(cropData);
      props.onSubmit?.(event);
      return;
    }

    setIsSubmitting(true);
    try {
      await addCrop();
      setValidated(false);
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || t("Error");
      swal(t("Error"), message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateCropData = (name, value) => {
    setCropData((previous) => ({ ...previous, [name]: value }));
  };

  const requiredFeedback = (
    <Form.Control.Feedback type="invalid">
      {t("required_field")}
    </Form.Control.Feedback>
  );

  const formFields = (
    <>
      {optionsError && (
        <div className="alert alert-danger" role="alert">
          {optionsError}
        </div>
      )}

      <Row className="sf-agri-form-section g-3">
        <Col lg="4" md="6" xs="12">
          <Form.Group controlId={isWizardMode ? "wizard-crop-type" : "crop-type"}>
            <Form.Label>{t("crop_type")} *</Form.Label>
            <Form.Select
              onChange={handleCropPick}
              value={cropData.cropType}
              required
              disabled={isOptionsLoading}
              aria-busy={isOptionsLoading}
            >
              <option value="">
                {isOptionsLoading ? t("loading") : t("Select_Crop")}
              </option>
              {listCrop.map((crop) => (
                <option key={crop.id} value={crop.id}>
                  {crop.crop}
                </option>
              ))}
            </Form.Select>
            {requiredFeedback}
          </Form.Group>
        </Col>

        <Col lg="4" md="6" xs="12">
          <Form.Group
            controlId={isWizardMode ? "wizard-crop-variety" : "crop-variety"}
          >
            <Form.Label>{t("crop_variety")} *</Form.Label>
            <Form.Select
              value={checked ? "" : cropData.variety}
              onChange={handleVarietyPick}
              required={!checked}
              disabled={!checked && !cropData.cropType}
            >
              <option value="">{t("crop_variety")}</option>
              {cropData.cropVariety.map((variety) => (
                <option key={variety.varietyId} value={variety.varietyId}>
                  {variety.variety}
                </option>
              ))}
            </Form.Select>
            <label
              className="sf-agri-check"
              htmlFor={isWizardMode ? "wizard-other-variety" : "other-variety"}
            >
              <input
                id={isWizardMode ? "wizard-other-variety" : "other-variety"}
                type="checkbox"
                checked={checked}
                onChange={(event) => {
                  setChecked(event.target.checked);
                  updateCropData("variety", "");
                }}
              />
              {t("other")}
            </label>
            {checked && (
              <Form.Control
                aria-label={t("crop_variety")}
                value={cropData.variety}
                placeholder={t("crop_variety")}
                required
                onChange={(event) =>
                  updateCropData("variety", event.target.value)
                }
              />
            )}
            {requiredFeedback}
          </Form.Group>
        </Col>

        {isWizardMode && (
          <Col lg="4" md="6" xs="12">
            <Form.Group controlId="wizard-crop-nature">
              <Form.Label>{t("culture_type")}</Form.Label>
              <Form.Control
                value={
                  cropData.crop_nature === 1
                    ? t("Culture_annuelle")
                    : cropData.crop_nature === 0
                    ? t("Culture_maraîchère")
                    : ""
                }
                disabled
                readOnly
              />
            </Form.Group>
          </Col>
        )}
      </Row>

      <Row className="sf-agri-form-section g-3">
        <Col lg="4" md="6" xs="12">
          <Form.Group
            controlId={isWizardMode ? "wizard-crop-field" : "crop-field"}
          >
            <Form.Label>{t("crop_field")} *</Form.Label>
            <FarmSelect
              defaultval={{
                value: parentFieldId || fieldId,
                label: parentFieldName || fieldName,
              }}
              url="/field/search-all-fields"
              onChange={(selected) => {
                setCropData((previous) => ({
                  ...previous,
                  field_uid: selected?.value || "",
                  zone_uid: "",
                }));
              }}
              placeholder={t("search_fields")}
              isDisabled={Boolean(isWizardMode && parentFieldId)}
            />
          </Form.Group>
        </Col>

        <Col lg="4" md="6" xs="12">
          <Form.Group
            controlId={isWizardMode ? "wizard-crop-zone" : "crop-zone"}
          >
            <Form.Label>{t("crop_zone")} *</Form.Label>
            <Form.Select
              value={cropData.zone_uid}
              onChange={(event) => updateCropData("zone_uid", event.target.value)}
              required
              disabled={Boolean(isWizardMode && parentZoneId)}
            >
              <option value="" disabled>
                {t("select_zone")}
              </option>
              {parentZoneId &&
                !zones.some((zone) => zone.uid === parentZoneId) && (
                  <option value={parentZoneId}>
                    {parentZoneName || parentZoneId}
                  </option>
                )}
              {zones.map((zone) => (
                <option key={zone.uid} value={zone.uid}>
                  {zone.name}
                </option>
              ))}
            </Form.Select>
            {requiredFeedback}
          </Form.Group>
        </Col>

        <Col lg="4" md="6" xs="12">
          <Form.Group
            controlId={isWizardMode ? "wizard-crop-surface" : "crop-surface"}
          >
            <Form.Label>{t("surface")} (m²) *</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              required
              value={cropData.surface}
              onChange={(event) => updateCropData("surface", event.target.value)}
              placeholder={t("surface")}
            />
            {requiredFeedback}
          </Form.Group>
        </Col>
      </Row>

      <Row className="sf-agri-form-section g-3">
        <Col lg="4" md="6" xs="12">
          <Form.Group
            controlId={isWizardMode ? "wizard-root-depth" : "root-depth"}
          >
            <Form.Label>{t("depth")} (m)</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={cropData.rootDepth}
              onChange={(event) =>
                updateCropData("rootDepth", event.target.value)
              }
              placeholder={t("depth")}
            />
          </Form.Group>
        </Col>

        <Col lg="4" md="6" xs="12">
          <Form.Group controlId={isWizardMode ? "wizard-days" : "crop-days"}>
            <Form.Label>{t("Days")}</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={cropData.days}
              onChange={(event) => updateCropData("days", event.target.value)}
              placeholder={t("Days")}
            />
          </Form.Group>
        </Col>

        <Col lg="4" md="6" xs="12">
          <Form.Group
            controlId={isWizardMode ? "wizard-planting-date" : "planting-date"}
          >
            <Form.Label>{t("planting_date")} *</Form.Label>
            <Form.Control
              type="date"
              required
              value={cropData.plantingDate}
              onChange={(event) =>
                updateCropData("plantingDate", event.target.value)
              }
            />
            {requiredFeedback}
          </Form.Group>
        </Col>
      </Row>

      <Row className="sf-agri-form-section g-3">
        <Col lg="4" md="6" xs="12">
          <Form.Group
            controlId={isWizardMode ? "wizard-growing-season" : "growing-season"}
          >
            <Form.Label>{t("growing_season")}</Form.Label>
            {isWizardMode ? (
              <Form.Control
                placeholder={t("dd/MM")}
                type="text"
                inputMode="numeric"
                value={displayDate}
                onChange={handleGrowingSeasonChange}
                maxLength={5}
              />
            ) : (
              <Form.Control
                type="date"
                value={cropData.growingDate}
                onChange={(event) =>
                  updateCropData("growingDate", event.target.value)
                }
              />
            )}
          </Form.Group>
        </Col>

        <Col lg="4" md="6" xs="12">
          <Form.Group
            controlId={isWizardMode ? "wizard-practical-fraction" : "practical-fraction"}
          >
            <Form.Label>{t("fraction_pratique")} (%)</Form.Label>
            <Form.Control
              type="number"
              min="0"
              max="100"
              value={cropData.ruPratique}
              onChange={(event) =>
                updateCropData("ruPratique", event.target.value)
              }
              placeholder={t("fraction_pratique")}
            />
          </Form.Group>
        </Col>

        <Col lg="4" md="6" xs="12">
          <Form.Group
            controlId={isWizardMode ? "wizard-ecart-inter" : "ecart-inter"}
          >
            <Form.Label>{t("ecart_inter")} (m) *</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              required
              value={cropData.ecartInter}
              onChange={(event) =>
                updateCropData("ecartInter", event.target.value)
              }
              placeholder={t("ecart_inter")}
            />
            {requiredFeedback}
          </Form.Group>
        </Col>

        <Col lg="4" md="6" xs="12">
          <Form.Group
            controlId={isWizardMode ? "wizard-ecart-intra" : "ecart-intra"}
          >
            <Form.Label>{t("ecart_intra")} (m) *</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              required
              value={cropData.ecartIntra}
              onChange={(event) =>
                updateCropData("ecartIntra", event.target.value)
              }
              placeholder={t("ecart_intra")}
            />
            {requiredFeedback}
          </Form.Group>
        </Col>

        <Col lg="4" md="6" xs="12">
          <Form.Group
            controlId={isWizardMode ? "wizard-density" : "crop-density"}
          >
            <Form.Label>{t("densité")} (plants/ha) *</Form.Label>
            <Form.Control
              type="number"
              min="0"
              required
              value={cropData.density}
              onChange={(event) => updateCropData("density", event.target.value)}
              placeholder={t("densité")}
            />
            {requiredFeedback}
          </Form.Group>
        </Col>
      </Row>
    </>
  );

  return (
    <Container
      className={`${isWizardMode ? "" : "p-md-5 p-3"} sf-agri-form`}
    >
      {!isWizardMode && (
        <div className="mb-4">
          <PageTitle subtitle={t("crop_info")} className="mb-3" />
          <div className="sf-agri-intro">
            <p className="sf-agri-help">{t("step_3_info")}</p>
          </div>
        </div>
      )}

      {isWizardMode ? (
        formFields
      ) : (
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          {formFields}
          <div className="d-flex justify-content-end">
            <Button
              type="submit"
              className="sf-agri-submit"
              disabled={isSubmitting || isOptionsLoading}
            >
              {isSubmitting ? t("loading") : t("submit_form")}
            </Button>
          </div>
        </Form>
      )}
    </Container>
  );
}
